import { useState, useCallback, useRef } from "react";
import {
  BulkUploadWSMessage,
  BulkUploadWSProgressMessage,
} from "@type/cv";

interface UseBulkUploadProgressReturn {
  connected: boolean;
  messages: BulkUploadWSProgressMessage[];
  latestMessage: BulkUploadWSProgressMessage | null;
  connect: () => void;
  disconnect: () => void;
}

export const useBulkUploadProgress = (
  userId: string,
  apiBaseUrl: string
): UseBulkUploadProgressReturn => {
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<BulkUploadWSProgressMessage[]>([]);
  const [latestMessage, setLatestMessage] =
    useState<BulkUploadWSProgressMessage | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const shouldReconnectRef = useRef(false);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    cleanup();

    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    setConnected(false);
  }, [cleanup]);

  const connect = useCallback(() => {
    if (!userId) return;

    // Clean up existing connection
    cleanup();
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    shouldReconnectRef.current = true;
    setMessages([]);
    setLatestMessage(null);

    const wsUrl = `${apiBaseUrl.replace("http", "ws")}/cv/bulk-upload/ws/${userId}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setConnected(true);

      // Set up ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data: BulkUploadWSMessage = JSON.parse(event.data);

        if ("type" in data && (data as any).type === "pong") {
          return;
        }

        if (data.event === "connected") {
          return;
        }

        if (
          data.event === "progress" ||
          data.event === "error" ||
          data.event === "completed" ||
          data.event === "cancelled"
        ) {
          setLatestMessage(data);
          setMessages((prev) => [...prev, data]);
        }
      } catch (error) {
        console.error("Error parsing bulk upload WS message:", error);
      }
    };

    ws.onerror = () => {
      setConnected(false);
    };

    ws.onclose = () => {
      setConnected(false);
      cleanup();

      // Only reconnect if explicitly intended
      if (shouldReconnectRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          if (shouldReconnectRef.current) {
            connect();
          }
        }, 3000);
      }
    };

    socketRef.current = ws;
  }, [userId, apiBaseUrl, cleanup]);

  return {
    connected,
    messages,
    latestMessage,
    connect,
    disconnect,
  };
};
